import { updateComplianceItem } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { LegacyDocumentType } from '@prisma/client';
import { Dispatch, SetStateAction, useState } from 'react';
import { Accept } from 'react-dropzone';
import { KeyedMutator } from 'swr';

type UploadFileModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  file: FileBasic;
  setFile: Dispatch<SetStateAction<FileBasic>>;
  documentType: LegacyDocumentType;
  title: string;
  onUploadSuccess?: () => void;
  accept?: Accept;
  isInputFile?: boolean;
  companyId: number;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
};

export const ComplianceItemFileUploadModal = ({
  isOpen,
  setIsOpen,
  file,
  setFile,
  documentType,
  onUploadSuccess,
  title = 'Upload file',
  accept,
  isInputFile = false,
  companyId,
  serviceRequest,
  mutate,
}: UploadFileModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setFile(null);
  };

  const upload = async () => {
    setIsSubmitting(true);

    const res = await updateComplianceItem(
      serviceRequest.complianceItemId,
      {
        ...(isInputFile
          ? { inputFiles: [{ ...file, documentType }] }
          : { acknowledgements: [{ ...file, documentType }] }),
      },
      undefined
    );

    if (!res.success) {
      setIsSubmitting(false);
      return;
    }

    if (onUploadSuccess) {
      await onUploadSuccess();
    }

    setIsSubmitting(false);
    handleClose();
    await mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent preventCloseOnOutsideClick className="flex flex-col">
        <DialogHeader className="flex flex-col items-start">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Please upload only a CSV, PDF, or Excel file.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6">
          <FieldWrapper>
            <Label>
              Upload file
              <span className="text-utility-violet-600">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              file={
                file
                  ? {
                      fileName: file?.fileName,
                      fileUrl: file?.fileUrl,
                      documentType,
                    }
                  : undefined
              }
              accept={accept}
              setFile={setFile}
              token={undefined}
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="w-full">
          <Button
            variant="secondary"
            onClick={handleClose}
            className="flex items-center gap-2"
          >
            Cancel
          </Button>

          <Button
            onClick={upload}
            disabled={isSubmitting || !file?.fileUrl}
            className="flex items-center gap-2"
          >
            {isSubmitting ? <LoadingIndicator /> : null} <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
